import type { ComponentType } from 'react';
import {
  Smartphone, LayoutDashboard, ShoppingCart, Receipt, RotateCcw, ClipboardList, Tag, Truck as TruckIcon,
  Package, Boxes, Warehouse, ArrowRightLeft, Hash, Layers, BadgePercent, ScanBarcode,
  Users, BookOpen, BadgePercent as LoyaltyIcon, Calculator, Landmark, CreditCard, CheckSquare,
  CalendarDays, FileText, Factory, Coins, DollarSign, Clock, UtensilsCrossed, LayoutGrid,
  Car, Settings2, Shield, Settings, ClipboardCheck, Building2, TrendingUp, Database, Webhook,
  Wallet, MessageSquare, Globe, Percent, ChefHat, MonitorSmartphone,
} from 'lucide-react';

import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { SalesPage } from '@/pages/sales/SalesPage';
import { ReturnsPage } from '@/pages/returns/ReturnsPage';
import { ShiftsPage } from '@/pages/shifts/ShiftsPage';
import { GiftCardsPage } from '@/pages/gift-cards/GiftCardsPage';
import { DeliveryPage } from '@/pages/delivery/DeliveryPage';
import { ProductsPage } from '@/pages/products/ProductsPage';
import { CategoriesPage } from '@/pages/categories/CategoriesPage';
import { BrandsPage } from '@/pages/brands/BrandsPage';
import { PriceTiersPage } from '@/pages/price-tiers/PriceTiersPage';
import { PromotionsPage } from '@/pages/promotions/PromotionsPage';
import { BarcodeLabelsPage } from '@/pages/barcode-labels/BarcodeLabelsPage';
import { CustomersPage } from '@/pages/customers/CustomersPage';
import { KhataPage } from '@/pages/customers/KhataPage';
import { CustomerDetailPage } from '@/pages/customers/CustomerDetailPage';
import { LoyaltySettingsPage } from '@/pages/loyalty/LoyaltySettingsPage';
import { InventoryPage } from '@/pages/inventory/InventoryPage';
import { StockTakePage } from '@/pages/inventory/StockTakePage';
import { SerialNumbersPage } from '@/pages/serial-numbers/SerialNumbersPage';
import { LocationsPage } from '@/pages/locations/LocationsPage';
import { StockTransfersPage } from '@/pages/stock-transfers/StockTransfersPage';
import { PurchasesPage } from '@/pages/purchases/PurchasesPage';
import { SuppliersPage } from '@/pages/suppliers/SuppliersPage';
import { VendorPaymentsPage } from '@/pages/vendor-payments/VendorPaymentsPage';
import { ExpensesPage } from '@/pages/expenses/ExpensesPage';
import { RecipesPage } from '@/pages/recipes/RecipesPage';
import { RecipeDetailPage } from '@/pages/recipes/RecipeDetailPage';
import { ProductionPage } from '@/pages/production/ProductionPage';
import { ProductionRunDetailPage } from '@/pages/production/ProductionRunDetailPage';
import { InstallmentsPage } from '@/pages/installments/InstallmentsPage';
import { ContractsPage } from '@/pages/contracts/ContractsPage';
import { RecoveryPage } from '@/pages/recovery/RecoveryPage';
import { DocumentsPage } from '@/pages/documents/DocumentsPage';
import { AccountingPage } from '@/pages/accounting/AccountingPage';
import { FiscalPeriodsPage } from '@/pages/accounting/FiscalPeriodsPage';
import { BankAccountsPage } from '@/pages/bank/BankAccountsPage';
import { BankReconciliationPage } from '@/pages/bank/BankReconciliationPage';
import { ChequesPage } from '@/pages/cheques/ChequesPage';
import { FbrPage } from '@/pages/fbr/FbrPage';
import { UsersPage } from '@/pages/users/UsersPage';
import { EmployeesPage } from '@/pages/employees/EmployeesPage';
import { TimeAttendancePage } from '@/pages/time-attendance/TimeAttendancePage';
import { PayrollPage } from '@/pages/payroll/PayrollPage';
import { TablesPage } from '@/pages/restaurant/TablesPage';
import { DiningOrdersPage } from '@/pages/restaurant/DiningOrdersPage';
import { OrderDetailPage } from '@/pages/restaurant/OrderDetailPage';
import { TakeawayQueuePage } from '@/pages/restaurant/TakeawayQueuePage';
import { ReservationsPage } from '@/pages/restaurant/ReservationsPage';
import { TipManagementPage } from '@/pages/restaurant/TipManagementPage';
import { OnlineOrderingWidgetPage } from '@/pages/restaurant/OnlineOrderingWidgetPage';
import { RolesPage } from '@/pages/roles/RolesPage';
import { AuditPage } from '@/pages/audit/AuditPage';
import { MobileLinksPage } from '@/pages/mobile-links/MobileLinksPage';
import { WebhooksPage } from '@/pages/webhooks/WebhooksPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { MyBillsPage } from '@/pages/settings/MyBillsPage';
import { TaxRatesPage } from '@/pages/tax-rates/TaxRatesPage';
import { CurrenciesPage } from '@/pages/settings/CurrenciesPage';
import { ProductImportTemplatePage } from '@/pages/product-import-template/ProductImportTemplatePage';
import { MenuModifiersPage } from '@/pages/restaurant/MenuModifiersPage';
import { RestaurantSetupPage } from '@/pages/restaurant/RestaurantSetupPage';
import { MasterDataHubPage } from '@/pages/admin/MasterDataHubPage';
import { CommunicationsPage } from '@/pages/admin/CommunicationsPage';

export type Icon = React.ElementType;

/**
 * A tab inside a workspace. `segment` is relative to the workspace `basePath`.
 * `permission` supports `|` (OR) and `&&` (AND) like the legacy nav. When omitted
 * the tab is always visible (e.g. Settings). `hidden` tabs are routable detail
 * pages (e.g. `:id`) that never appear in the workspace tab bar.
 */
export interface WorkspaceTab {
  key: string;
  labelKey: string;
  segment: string;
  Component: ComponentType;
  permission?: string;
  icon?: Icon;
  hidden?: boolean;
}

export interface Workspace {
  id: string;
  labelKey: string;
  icon: Icon;
  basePath: string;
  /** Default tab segment used by the workspace index redirect. */
  defaultSegment: string;
  tabs: WorkspaceTab[];
}

/** A pinned single-screen entry that links directly (no tab bar). */
export interface PinnedEntry {
  id: string;
  labelKey: string;
  icon: Icon;
  href: string;
  permission?: string;
  /** Opens in a new browser tab (POS terminal). */
  external?: boolean;
  highlight?: boolean;
  superAdminOnly?: boolean;
}

export const WORKSPACES: Workspace[] = [
  {
    id: 'sell',
    labelKey: 'sell',
    icon: ShoppingCart,
    basePath: '/sell',
    defaultSegment: 'sales',
    tabs: [
      { key: 'sales', labelKey: 'salesHistory', segment: 'sales', Component: SalesPage, permission: 'pos:read', icon: Receipt },
      { key: 'returns', labelKey: 'returns', segment: 'returns', Component: ReturnsPage, permission: 'returns:read', icon: RotateCcw },
      { key: 'shifts', labelKey: 'cashierShifts', segment: 'shifts', Component: ShiftsPage, permission: 'shifts:read', icon: ClipboardList },
      { key: 'gift-cards', labelKey: 'giftCards', segment: 'gift-cards', Component: GiftCardsPage, permission: 'gift-cards:read', icon: Tag },
      { key: 'delivery', labelKey: 'delivery', segment: 'delivery', Component: DeliveryPage, permission: 'delivery:read', icon: TruckIcon },
    ],
  },
  {
    id: 'restaurant',
    labelKey: 'restaurant',
    icon: UtensilsCrossed,
    basePath: '/restaurant',
    defaultSegment: 'floor',
    tabs: [
      { key: 'floor', labelKey: 'floorPlan', segment: 'floor', Component: TablesPage, permission: 'restaurant:read', icon: LayoutGrid },
      { key: 'orders', labelKey: 'activeOrders', segment: 'orders', Component: DiningOrdersPage, permission: 'restaurant:read', icon: ClipboardList },
      { key: 'order-detail', labelKey: 'activeOrders', segment: 'orders/:id', Component: OrderDetailPage, permission: 'restaurant:read', hidden: true },
      { key: 'takeaway', labelKey: 'takeawayQueue', segment: 'takeaway', Component: TakeawayQueuePage, permission: 'restaurant:read', icon: Car },
      { key: 'reservations', labelKey: 'reservations', segment: 'reservations', Component: ReservationsPage, permission: 'reservations:read', icon: CalendarDays },
      { key: 'tips', labelKey: 'tips', segment: 'tips', Component: TipManagementPage, permission: 'restaurant:read', icon: Coins },
      { key: 'online-ordering', labelKey: 'onlineOrdering', segment: 'online-ordering', Component: OnlineOrderingWidgetPage, permission: 'online-ordering:read', icon: Globe },
    ],
  },
  {
    id: 'catalog',
    labelKey: 'catalog',
    icon: Package,
    basePath: '/catalog',
    defaultSegment: 'products',
    tabs: [
      { key: 'products', labelKey: 'products', segment: 'products', Component: ProductsPage, permission: 'products:read', icon: Package },
      { key: 'categories', labelKey: 'categories', segment: 'categories', Component: CategoriesPage, permission: 'products:read', icon: Tag },
      { key: 'brands', labelKey: 'brands', segment: 'brands', Component: BrandsPage, permission: 'products:read', icon: Tag },
      { key: 'price-tiers', labelKey: 'priceTiers', segment: 'price-tiers', Component: PriceTiersPage, permission: 'price-tiers:read', icon: Layers },
      { key: 'promotions', labelKey: 'promotions', segment: 'promotions', Component: PromotionsPage, permission: 'promotions:read', icon: BadgePercent },
      { key: 'barcode-labels', labelKey: 'barcodeLabels', segment: 'barcode-labels', Component: BarcodeLabelsPage, permission: 'barcode-labels:print', icon: ScanBarcode },
    ],
  },
  {
    id: 'customers',
    labelKey: 'customers',
    icon: Users,
    basePath: '/customers',
    defaultSegment: 'list',
    tabs: [
      { key: 'list', labelKey: 'customers', segment: 'list', Component: CustomersPage, permission: 'customers:read', icon: Users },
      { key: 'khata', labelKey: 'khata', segment: 'khata', Component: KhataPage, permission: 'customers:read', icon: BookOpen },
      { key: 'loyalty', labelKey: 'loyaltyProgram', segment: 'loyalty', Component: LoyaltySettingsPage, permission: 'loyalty:read', icon: LoyaltyIcon },
      { key: 'detail', labelKey: 'customers', segment: ':id', Component: CustomerDetailPage, permission: 'customers:read', hidden: true },
      { key: 'detail-ledger', labelKey: 'customers', segment: ':id/ledger', Component: CustomerDetailPage, permission: 'customers:read', hidden: true },
    ],
  },
  {
    id: 'inventory',
    labelKey: 'inventory',
    icon: Boxes,
    basePath: '/inventory',
    defaultSegment: 'stock',
    tabs: [
      { key: 'stock', labelKey: 'inventory', segment: 'stock', Component: InventoryPage, permission: 'inventory:read', icon: Boxes },
      { key: 'stock-takes', labelKey: 'stockTakes', segment: 'stock-takes', Component: StockTakePage, permission: 'inventory:read', icon: ClipboardCheck },
      { key: 'serial-numbers', labelKey: 'serialNumbers', segment: 'serial-numbers', Component: SerialNumbersPage, permission: 'serial-numbers:read', icon: Hash },
      { key: 'locations', labelKey: 'locationsWarehouses', segment: 'locations', Component: LocationsPage, permission: 'locations:read', icon: Warehouse },
      { key: 'transfers', labelKey: 'stockTransfers', segment: 'transfers', Component: StockTransfersPage, permission: 'transfers:read', icon: ArrowRightLeft },
    ],
  },
  {
    id: 'purchasing',
    labelKey: 'purchasing',
    icon: TruckIcon,
    basePath: '/purchasing',
    defaultSegment: 'orders',
    tabs: [
      { key: 'orders', labelKey: 'purchases', segment: 'orders', Component: PurchasesPage, permission: 'purchases:read', icon: ClipboardList },
      { key: 'suppliers', labelKey: 'suppliers', segment: 'suppliers', Component: SuppliersPage, permission: 'purchases:read', icon: Users },
      { key: 'vendor-payments', labelKey: 'vendorPayments', segment: 'vendor-payments', Component: VendorPaymentsPage, permission: 'bank:read', icon: CreditCard },
      { key: 'expenses', labelKey: 'expenses', segment: 'expenses', Component: ExpensesPage, permission: 'expenses:read', icon: Wallet },
    ],
  },
  {
    id: 'manufacturing',
    labelKey: 'manufacturing',
    icon: Factory,
    basePath: '/manufacturing',
    defaultSegment: 'recipes',
    tabs: [
      { key: 'recipes', labelKey: 'recipesBoMs', segment: 'recipes', Component: RecipesPage, permission: 'recipes:read', icon: BookOpen },
      { key: 'recipe-detail', labelKey: 'recipesBoMs', segment: 'recipes/:id', Component: RecipeDetailPage, permission: 'recipes:read', hidden: true },
      { key: 'production', labelKey: 'productionRuns', segment: 'production', Component: ProductionPage, permission: 'production:read', icon: Factory },
      { key: 'production-detail', labelKey: 'productionRuns', segment: 'production/:id', Component: ProductionRunDetailPage, permission: 'production:read', hidden: true },
    ],
  },
  {
    id: 'lending',
    labelKey: 'lending',
    icon: CreditCard,
    basePath: '/lending',
    defaultSegment: 'installments',
    tabs: [
      { key: 'installments', labelKey: 'installmentsBnpl', segment: 'installments', Component: InstallmentsPage, permission: 'installments:read | pos:read', icon: CreditCard },
      { key: 'contracts', labelKey: 'contracts', segment: 'contracts', Component: ContractsPage, permission: 'installments:read', icon: FileText },
      { key: 'recovery', labelKey: 'recovery', segment: 'recovery', Component: RecoveryPage, permission: 'installments:read | pos:read', icon: Coins },
      { key: 'documents', labelKey: 'documents', segment: 'documents', Component: DocumentsPage, permission: 'documents:read | documents:*', icon: FileText },
    ],
  },
  {
    id: 'finance',
    labelKey: 'finance',
    icon: TrendingUp,
    basePath: '/finance',
    defaultSegment: 'accounting',
    tabs: [
      { key: 'accounting', labelKey: 'accounting', segment: 'accounting', Component: AccountingPage, permission: 'accounting:read', icon: Calculator },
      { key: 'bank-accounts', labelKey: 'bankAccounts', segment: 'bank-accounts', Component: BankAccountsPage, permission: 'bank:read', icon: Landmark },
      { key: 'bank-reconciliation', labelKey: 'bankReconciliation', segment: 'bank-reconciliation', Component: BankReconciliationPage, permission: 'bank:read', icon: ArrowRightLeft },
      { key: 'cheques', labelKey: 'cheques', segment: 'cheques', Component: ChequesPage, permission: 'cheques:read', icon: CheckSquare },
      { key: 'fiscal-periods', labelKey: 'fiscalPeriods', segment: 'fiscal-periods', Component: FiscalPeriodsPage, permission: 'accounting:read', icon: CalendarDays },
      { key: 'fbr', labelKey: 'fbr', segment: 'fbr', Component: FbrPage, permission: 'fbr:read', icon: Receipt },
    ],
  },
  {
    id: 'people',
    labelKey: 'people',
    icon: Users,
    basePath: '/people',
    defaultSegment: 'employees',
    tabs: [
      { key: 'employees', labelKey: 'employees', segment: 'employees', Component: EmployeesPage, permission: 'employees:read', icon: Users },
      { key: 'attendance', labelKey: 'timeAttendance', segment: 'attendance', Component: TimeAttendancePage, permission: 'time-attendance:read', icon: Clock },
      { key: 'payroll', labelKey: 'payroll', segment: 'payroll', Component: PayrollPage, permission: 'payroll:read', icon: DollarSign },
    ],
  },
  {
    id: 'admin',
    labelKey: 'administration',
    icon: Settings2,
    basePath: '/admin',
    defaultSegment: 'settings',
    tabs: [
      { key: 'users', labelKey: 'users', segment: 'users', Component: UsersPage, permission: 'users:read', icon: Users },
      { key: 'roles', labelKey: 'roles', segment: 'roles', Component: RolesPage, permission: 'users:read', icon: Shield },
      { key: 'master-data', labelKey: 'masterData', segment: 'master-data', Component: MasterDataHubPage, icon: Database },
      // POS-I112: visible so an owner can find where to configure tax rates (was hidden behind the Master Data hub).
      { key: 'md-tax-rates', labelKey: 'taxRates', segment: 'master-data/tax-rates', Component: TaxRatesPage, permission: 'tax-rates:read', icon: Percent },
      { key: 'md-currencies', labelKey: 'currencies', segment: 'master-data/currencies', Component: CurrenciesPage, permission: 'multi-currency:read', hidden: true },
      { key: 'md-templates', labelKey: 'templates', segment: 'master-data/templates', Component: ProductImportTemplatePage, permission: 'product-templates:read', hidden: true },
      { key: 'md-menu-modifiers', labelKey: 'menuModifiers', segment: 'master-data/menu-modifiers', Component: MenuModifiersPage, permission: 'restaurant:write', hidden: true },
      { key: 'md-restaurant-setup', labelKey: 'restaurantSetup', segment: 'master-data/restaurant-setup', Component: RestaurantSetupPage, permission: 'restaurant:write', hidden: true },
      { key: 'mobile-links', labelKey: 'mobileLinks', segment: 'mobile-links', Component: MobileLinksPage, permission: 'mobile-links:read', icon: Smartphone },
      { key: 'integrations', labelKey: 'integrations', segment: 'integrations', Component: WebhooksPage, permission: 'api:read', icon: Webhook },
      { key: 'communications', labelKey: 'communications', segment: 'communications', Component: CommunicationsPage, permission: 'communications:read', icon: MessageSquare },
      { key: 'audit', labelKey: 'auditLog', segment: 'audit', Component: AuditPage, permission: 'audit:read', icon: ClipboardList },
      { key: 'billing', labelKey: 'myBills', segment: 'billing', Component: MyBillsPage, permission: 'billing:read', icon: Receipt },
      { key: 'settings', labelKey: 'settings', segment: 'settings', Component: SettingsPage, icon: Settings },
    ],
  },
];

/** Pinned single-screen entries rendered above the workspace list. */
export const PINNED_TOP: PinnedEntry[] = [
  { id: 'home', labelKey: 'home', icon: LayoutDashboard, href: '/dashboard' },
  { id: 'pos', labelKey: 'pos', icon: ShoppingCart, href: '/pos', external: true, highlight: true, permission: 'pos:create' },
  // POS-I314 / POS-I315: full-screen restaurant tools had no entry point.
  { id: 'kitchen', labelKey: 'kitchen', icon: ChefHat, href: '/kitchen', external: true, permission: 'kitchen:read' },
  { id: 'kiosk', labelKey: 'selfServiceKiosk', icon: MonitorSmartphone, href: '/kiosk', external: true, permission: 'pos:create' },
];

/** Reports — a workspace with no internal tab bar, links straight to one page. */
export const PINNED_REPORTS: PinnedEntry = {
  id: 'reports', labelKey: 'reports', icon: FileText, href: '/reports', permission: 'reports:read',
};

export const PINNED_SYSTEM: PinnedEntry[] = [
  { id: 'store-guide', labelKey: 'storeGuide', icon: BookOpen, href: '/store-guide' },
  { id: 'platform', labelKey: 'platform', icon: Building2, href: '/platform', superAdminOnly: true },
];

/** Evaluate a permission expression supporting `|` (OR) and `&&` (AND). */
export function matchesPermission(
  permission: string | undefined,
  hasPermission: (p: string) => boolean,
): boolean {
  if (!permission) return true;
  if (permission.includes('&&')) {
    return permission.split('&&').every((p) => hasPermission(p.trim()));
  }
  return permission.split('|').some((p) => hasPermission(p.trim()));
}

/** Tabs that appear in the workspace tab bar for the current user (excludes hidden). */
export function visibleTabs(
  ws: Workspace,
  hasPermission: (p: string) => boolean,
): WorkspaceTab[] {
  const seen = new Set<string>();
  return ws.tabs
    .filter((t) => !t.hidden && matchesPermission(t.permission, hasPermission))
    .filter((t) => {
      const key = `${t.segment}::${t.labelKey}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

/** First tab (by config order) the user is allowed to open, or undefined. */
export function getFirstAccessibleTab(
  ws: Workspace,
  hasPermission: (p: string) => boolean,
): WorkspaceTab | undefined {
  return visibleTabs(ws, hasPermission)[0];
}

/** Whole-workspace visibility: visible when at least one tab is permitted. */
export function isWorkspaceVisible(
  ws: Workspace,
  hasPermission: (p: string) => boolean,
): boolean {
  return visibleTabs(ws, hasPermission).length > 0;
}

/**
 * POS-I107: THE one rule for which workspaces exist for a user - a workspace is listed (sidebar,
 * command palette, any shortcut) only when at least one of its visible tabs survives the permission
 * check, and the contextual tab menu uses the very same `visibleTabs`. Callers must recompute when the
 * user's permissions change (see the `permissions` dependency in DashboardLayout).
 */
export function visibleWorkspaces(
  hasPermission: (p: string) => boolean,
  workspaces: Workspace[] = WORKSPACES,
): Workspace[] {
  return workspaces.filter((ws) => isWorkspaceVisible(ws, hasPermission));
}

/** Resolve the workspace whose basePath prefixes the given pathname. */
export function getWorkspaceByPath(pathname: string): Workspace | undefined {
  return WORKSPACES.find(
    (ws) => pathname === ws.basePath || pathname.startsWith(`${ws.basePath}/`),
  );
}

const STANDALONE_NAV_TITLE_KEYS: Record<string, string> = {
  '/dashboard': 'home',
  '/reports': 'reports',
  '/settings': 'settings',
  '/store-guide': 'storeGuide',
  '/platform': 'platform',
  '/pos': 'pos',
  '/kitchen': 'kitchen',
};

/** i18n `nav` labelKey for the current authenticated screen (best-effort). */
export function getNavLabelKeyForPath(pathname: string): string | undefined {
  const standalone = STANDALONE_NAV_TITLE_KEYS[pathname];
  if (standalone) return standalone;

  const ws = getWorkspaceByPath(pathname);
  if (!ws) return undefined;

  const relative =
    pathname === ws.basePath ? '' : pathname.slice(ws.basePath.length + 1);

  if (!relative) {
    const firstVisible = ws.tabs.find((tab) => !tab.hidden);
    return firstVisible?.labelKey ?? ws.labelKey;
  }

  const tabsBySpecificity = [...ws.tabs].sort(
    (a, b) => b.segment.length - a.segment.length,
  );
  for (const tab of tabsBySpecificity) {
    if (tab.segment.includes(':')) {
      const pattern = `^${tab.segment.replace(/:[^/]+/g, '[^/]+')}$`;
      if (new RegExp(pattern).test(relative)) return tab.labelKey;
    } else if (
      relative === tab.segment ||
      relative.startsWith(`${tab.segment}/`)
    ) {
      return tab.labelKey;
    }
  }

  return ws.labelKey;
}

/**
 * Legacy → new path redirects so existing deep links keep working after the
 * nested-route migration. Exact-path matches only; param detail routes use
 * `LEGACY_DETAIL_REDIRECTS`. App.tsx preserves search, hash, and location.state.
 */
export const LEGACY_REDIRECTS: Record<string, string> = {
  '/sales': '/sell/sales',
  '/returns': '/sell/returns',
  '/shifts': '/sell/shifts',
  '/gift-cards': '/sell/gift-cards',
  '/delivery': '/sell/delivery',
  '/products': '/catalog/products',
  '/categories': '/catalog/categories',
  '/brands': '/catalog/brands',
  '/price-tiers': '/catalog/price-tiers',
  '/promotions': '/catalog/promotions',
  '/barcode-labels': '/catalog/barcode-labels',
  '/khata': '/customers/khata',
  '/loyalty': '/customers/loyalty',
  '/stock-takes': '/inventory/stock-takes',
  '/serial-numbers': '/inventory/serial-numbers',
  '/locations': '/inventory/locations',
  '/stock-transfers': '/inventory/transfers',
  '/purchases': '/purchasing/orders',
  '/suppliers': '/purchasing/suppliers',
  '/vendor-payments': '/purchasing/vendor-payments',
  '/expenses': '/purchasing/expenses',
  '/recipes': '/manufacturing/recipes',
  '/production': '/manufacturing/production',
  '/installments': '/lending/installments',
  '/contracts': '/lending/contracts',
  '/recovery': '/lending/recovery',
  '/documents': '/lending/documents',
  '/accounting': '/finance/accounting',
  '/fiscal-periods': '/finance/fiscal-periods',
  '/bank-accounts': '/finance/bank-accounts',
  '/bank-reconciliation': '/finance/bank-reconciliation',
  '/cheques': '/finance/cheques',
  '/fbr': '/finance/fbr',
  '/currencies': '/admin/master-data/currencies',
  '/tax-rates': '/admin/master-data/tax-rates',
  '/product-templates': '/admin/master-data/templates',
  '/time-attendance': '/people/attendance',
  '/payroll': '/people/payroll',
  '/users': '/admin/users',
  '/roles': '/admin/roles',
  '/audit': '/admin/audit',
  '/webhooks': '/admin/integrations',
  '/restaurant/tables': '/restaurant/floor',
  '/restaurant/menu-modifiers': '/admin/master-data/menu-modifiers',
  '/restaurant/setup': '/admin/master-data/restaurant-setup',
};

/**
 * Legacy `/:resource/:id` URLs that must land on the workspace detail page,
 * not the list. `toPrefix` + `/${id}` is the canonical path.
 */
export const LEGACY_DETAIL_REDIRECTS: { from: string; toPrefix: string }[] = [
  { from: '/recipes/:id', toPrefix: '/manufacturing/recipes' },
  { from: '/production/:id', toPrefix: '/manufacturing/production' },
];

/** DashboardPage re-exported for the canonical `/dashboard` (Home) route. */
export { DashboardPage };
